import type { IconType } from "react-icons";
import {
  FiCalendar,
  FiCompass,
  FiLifeBuoy,
  FiMonitor,
  FiPackage,
  FiPieChart,
  FiRotateCcw,
  FiShield,
  FiShoppingCart,
  FiSmartphone,
  FiTag,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import type { ClipId } from "./faqClips";

// Every answer here must match what the app, website and API actually do.
// Check the code before changing a fact; plan limits must match DueDash
// src/utils/subscriptionLimits.ts and PricingPage/plans.ts.

export type FaqTag =
  | "App"
  | "Website"
  | "All plans"
  | "Lite & Pro"
  | "Pro"
  | "Owners"
  | "Employees";

export interface FaqItem {
  /** Anchor for links: /faq#<id>. Never rename one that other pages link to. */
  id: string;
  question: string;
  /** Plain-language answer, a sentence or three. */
  answer: string;
  steps?: string[];
  /** For people who want to know exactly how it works. */
  details?: string[];
  /** Where to find it, e.g. "App › Settings › Billing". */
  where?: string;
  tags?: FaqTag[];
  clip?: ClipId;
  /** Extra words people might search for. */
  keywords?: string;
}

export interface FaqTopic {
  /** Anchor for the topic. "square" is linked from Sales Tax Reports. */
  id: string;
  title: string;
  blurb: string;
  icon: IconType;
  items: FaqItem[];
}

export const FAQ_TOPICS: FaqTopic[] = [
  {
    id: "getting-started",
    title: "Getting started",
    blurb: "What VendorOrbis is, signing up, and where things live.",
    icon: FiCompass,
    items: [
      {
        id: "what-is-vendororbis",
        question: "What is VendorOrbis?",
        answer:
          "A point-of-sale and back office for vendors at craft fairs, markets, festivals and pop-ups. You sell at the booth with the iPhone or Android app, and every sale is tied to the event and the stock you brought, so you know what each show made before you pack up.",
        tags: ["App", "Website", "All plans"],
        keywords: "pos point of sale craft fair market vendor booth",
      },
      {
        id: "sign-up",
        question: "How do I create an account?",
        answer:
          "Download the app or click Start for free on vendororbis.com, enter your name, email and a password, then type in the 6-digit code we email you. You start on the Free plan with no credit card.",
        details: [
          "The verification code expires after 1 hour. On the website you can ask for a new one every 30 seconds.",
          "You can't sign in until your email is verified, on the website or in the app.",
          "One login works everywhere: the same email and password on your phone, tablet and the website.",
        ],
        tags: ["App", "Website", "All plans"],
        keywords: "register signup verification code email verify",
      },
      {
        id: "app-vs-website",
        question: "What's the difference between the app and the website?",
        answer:
          "The app is for running your booth: checkout, Square and Tap to Pay, events, inventory, finance and Insight. The website is your back office on a bigger screen, with a few tools that only live there. Both use the same account and data.",
        details: [
          "Checkout (ringing up sales) is in the app only.",
          "Website only: importing inventory from Excel, monthly sales tax reports, the PDF editor, the booth layout planner and TikTok live auctions.",
          "Changes sync through your account, so an item edited on the website shows up in the app next time it refreshes.",
        ],
        tags: ["App", "Website"],
        keywords: "web back office desktop computer",
      },
      {
        id: "main-screens",
        question: "Where are the main screens in the app?",
        answer:
          "The bottom tabs are Events, Finance, Inventory and Insight. The menu (top left) has Settings, Discounts, Categories, Help and About. You can choose which tab opens first in Settings › Preferences › Default Dashboard.",
        where: "App › bottom tabs and ☰ menu",
        tags: ["App"],
        keywords: "tabs navigation menu dashboard home",
      },
      {
        id: "devices",
        question: "Can I use more than one phone?",
        answer:
          "Yes. Sign in with the same account on as many phones or tablets as you like. Sales from every device land in the same events and reports.",
        details: [
          "Stock is reduced on the server for each sale, so two phones selling at the same event can't both sell the last item.",
          "Each phone keeps its own offline queue; see Selling offline.",
        ],
        tags: ["App"],
        keywords: "multiple devices tablet ipad two phones",
      },
    ],
  },
  {
    id: "plans",
    title: "Plans & billing",
    blurb: "What each plan includes, trials, and managing your subscription.",
    icon: FiTag,
    items: [
      {
        id: "plans-compare",
        question: "What does each plan include?",
        answer:
          "Free covers up to 10 active events and 25 inventory items. Lite ($9.99/month) raises that to 50 events and 250 items. Pro ($24.99/month) is unlimited and adds inventory pools, employees with staff pay, and Insight. Square payments are included on every plan.",
        details: [
          "“Active events” are events you haven't archived. Archive a finished show to free a slot.",
          "Limits are checked by our server when you create an event or item, so they're the same in the app and on the website.",
          "Pro adds inventory pools, employees and staff pay, and the Insight business advisor with restock forecasts.",
          "TikTok live auction tools on the website need a paid plan (Lite or Pro).",
        ],
        tags: ["All plans"],
        keywords: "price pricing cost free lite pro limits compare",
      },
      {
        id: "free-trial",
        question: "Is there a free trial?",
        answer:
          "Pro monthly starts with a 30-day free trial for new subscribers. Nothing is charged today; if you don't cancel before the trial ends, it renews at $24.99/month.",
        details: [
          "The trial is on Pro monthly only, once per customer.",
          "You can cancel during the trial and keep Pro until it ends.",
        ],
        tags: ["Pro"],
        keywords: "trial 30 days free pro",
      },
      {
        id: "yearly-price",
        question: "Is there a yearly discount?",
        answer:
          "Yes, on the website: Lite is $99/year and Pro is $249/year, about two months free. Yearly plans bought in the App Store or Google Play are $119 and $299.",
        tags: ["Website", "Lite & Pro"],
        keywords: "annual yearly discount save",
      },
      {
        id: "where-to-subscribe",
        question: "Where do I subscribe, and where do I manage it?",
        answer:
          "Subscribe on the website, in the App Store or in Google Play. Your plan works everywhere either way. You manage it wherever you bought it: website subscriptions from Billing on vendororbis.com, store subscriptions in your Apple or Google account.",
        steps: [
          "Website: vendororbis.com › Billing › Manage subscription.",
          "iPhone: Settings › your name › Subscriptions › VendorOrbis.",
          "Android: Google Play › Profile › Payments & subscriptions › Subscriptions.",
        ],
        details: [
          "The Manage button in the app opens the right place for you, including the website for website subscribers.",
          "Don't subscribe a second time somewhere else: you'd be billed twice. Change plans where you first subscribed.",
          "Restore Purchases in the app reconnects an App Store or Google Play subscription to your account.",
        ],
        tags: ["App", "Website", "Lite & Pro"],
        keywords: "manage cancel change plan upgrade downgrade restore app store google play stripe",
      },
      {
        id: "cancel",
        question: "What happens if I cancel or downgrade?",
        answer:
          "You keep your plan until the end of the period you paid for, then move to the lower plan. Nothing is deleted. If you have more events or items than the new plan allows, you just can't add new ones until you're under the limit.",
        details: [
          "Payments are non-refundable except where the law requires. App Store and Google Play refunds are handled by Apple or Google.",
          "If you think you were charged by mistake, email support@vendororbis.com.",
        ],
        tags: ["Lite & Pro"],
        keywords: "cancel refund downgrade end subscription",
      },
    ],
  },
  {
    id: "events",
    title: "Events",
    blurb: "Creating shows, multi-day schedules, and closing out.",
    icon: FiCalendar,
    items: [
      {
        id: "create-event",
        question: "How do I create an event?",
        answer:
          "On the Events tab, tap the + button. Add the title, dates and times, and location, then check the tax rate. For a multi-day show, each day gets its own hours. Picking a past event as a suggestion copies its setup.",
        where: "App › Events › +",
        details: [
          "Each event has its own sales tax rate, used at checkout.",
          "If Square is connected, you can pick which Square location the event's card payments go to.",
          "Pro owners can assign employees to the event here.",
        ],
        tags: ["App", "Website", "Owners"],
        clip: "create-event",
        keywords: "new event show market add schedule multi-day",
      },
      {
        id: "event-tabs",
        question: "What's inside an event?",
        answer:
          "Three tabs. Info has the details, weather and packing list. Sales is your checkout. Transactions lists every sale and refund, with the Staff Pay card if employees worked it.",
        where: "App › Events › an event",
        tags: ["App"],
        keywords: "info sales transactions tabs",
      },
      {
        id: "allocate-stock",
        question: "How do I bring stock to an event?",
        answer:
          "Allocate it: open the event's Sales tab and add items with the quantity you're bringing. That stock moves from your inventory to the event, and the event tracks what's left and what sold.",
        details: [
          "Each event keeps allocated, remaining and sold counts per item.",
          "You can only sell what's allocated to that event, so stock counts stay right across shows.",
          "Unsold stock goes back to your inventory when you finalize the event.",
        ],
        tags: ["App", "Owners"],
        clip: "allocate-stock",
        keywords: "allocate stock bring inventory event quantity",
      },
      {
        id: "finalize-event",
        question: "What does Finalize do? And Archive?",
        answer:
          "Finalize closes the event when the show is over: checkout stops and unsold stock goes back to your inventory. Reopen undoes it. Archive hides the event from your list and frees a slot on your plan's event limit.",
        where: "App › an event › Info › tap the ACTIVE badge › Completed",
        details: [
          "Sales are accepted only while an event is active and not archived, including from the offline queue.",
          "Archived events still count in your finance history.",
        ],
        tags: ["App", "Owners"],
        clip: "finalize-event",
        keywords: "finalize close reopen archive delete event limit",
      },
      {
        id: "packing-list",
        question: "How do the weather and packing list work?",
        answer:
          "For outdoor events, Info shows the forecast and a packing list built from it: canopy weights, rain gear, sun and heat supplies, or warm layers. Mark an event as indoor and the weather gear is skipped. Tick items off as you pack.",
        details: [
          "Forecasts are available up to 15 days ahead.",
          "Canopy weights: at least 25 lb per leg; 40 lb per leg plus tie-downs when gusts reach 20 mph; a take-down plan at 35 mph and up.",
          "Rain gear from a 30% chance of rain; heat supplies at 85°F and up (or a high UV index); warm layers at 50°F and below.",
        ],
        tags: ["App"],
        clip: "packing-list",
        keywords: "weather wind rain canopy tent packing checklist forecast",
      },
      {
        id: "event-reminders",
        question: "Can the app remind me before an event?",
        answer:
          "Yes. Choose no reminder, 1 hour, 1 day or 3 days before, in Settings › Preferences. Allow notifications when your phone asks.",
        where: "App › Settings › Preferences",
        tags: ["App"],
        keywords: "reminder notification push alert",
      },
    ],
  },
  {
    id: "checkout",
    title: "Selling & checkout",
    blurb: "Ringing up sales, payment methods, discounts, fees and offline mode.",
    icon: FiShoppingCart,
    items: [
      {
        id: "make-a-sale",
        question: "How do I ring up a sale?",
        answer:
          "Open the event, go to Sales, tap items to add them, then open the cart. Check quantities and discounts, tap to pay, and pick how the customer paid. You'll see Payment Successful when it's recorded.",
        where: "App › Events › an event › Sales",
        tags: ["App"],
        clip: "cash-sale",
        keywords: "sell checkout cart ring up sale pay",
      },
      {
        id: "payment-methods",
        question: "Which payment methods can I take?",
        answer:
          "Cash, card through Square (reader or Tap to Pay), manual card for payments taken on another terminal, and your own QR methods like Venmo, Cash App or Zelle. You can split one sale across several methods.",
        details: [
          "Manual card only records the payment. It doesn't charge the card, so take the payment on your terminal first.",
          "QR methods show the code you uploaded. Check the payment arrived in that app before you confirm.",
          "Set up methods and their QR codes in Settings › Billing.",
        ],
        tags: ["App", "All plans"],
        clip: "split-payment",
        keywords: "cash card venmo zelle cash app paypal qr split payment manual",
      },
      {
        id: "discounts",
        question: "How do discounts work?",
        answer:
          "Add a discount to any item in the cart: a dollar amount off, a percentage off, or a bulk deal like 3 for $25. Save the ones you use often under Menu › Discounts.",
        details: [
          "All money is calculated in whole cents, the same way on the phone and the server, so totals always match.",
          "A discount can never take an item below $0.",
          "Bulk deals price each full group at the deal price and any leftovers at the regular price (default group size 3).",
        ],
        tags: ["App", "All plans"],
        clip: "discount",
        keywords: "discount coupon sale percent off bundle bulk deal",
      },
      {
        id: "card-fees",
        question: "Can I pass card fees on to customers?",
        answer:
          "Yes. In Settings › Billing, set a card fee as a percentage, a flat amount, or a percentage plus a fixed amount. It's added to card payments, and you can skip it for a single sale at checkout.",
        where: "App › Settings › Billing › Card fee",
        tags: ["App", "Owners"],
        keywords: "surcharge processing fee card fee convenience",
      },
      {
        id: "sales-tax",
        question: "How is sales tax calculated?",
        answer:
          "Each event has a tax rate, and checkout adds it to taxable items. When a customer pays through Square, the tax Square calculated is what's recorded, so your records match Square.",
        details: [
          "Tax is worked out in cents per sale and saved with a breakdown by rate.",
          "Monthly sales tax reports are on the website under Finance › Tax reports.",
        ],
        tags: ["App", "All plans"],
        keywords: "tax rate sales tax vat",
      },
      {
        id: "offline",
        question: "Can I sell without internet?",
        answer:
          "Yes, for cash, QR and manual card payments. If there's no signal, the sale is saved on your phone (you'll see Saved offline). Once you have signal again, close the app fully and reopen it to send saved sales. Square card payments need an internet connection.",
        details: [
          "Each sale carries a unique ID, so a sale that's sent twice is only recorded once.",
          "Saved sales are sent, oldest first, each time the app starts with a connection. If one fails, the rest wait so the order is kept.",
          "Saved sales stay on the phone even if you sign out. Don't delete the app until they've been sent.",
          "A sale that fails five times stops retrying but stays on the phone. Contact support and we'll help recover it.",
        ],
        tags: ["App", "All plans"],
        keywords: "offline no signal no internet wifi sync queue",
      },
    ],
  },
  {
    id: "square",
    title: "Square & Tap to Pay",
    blurb: "Connecting Square, locations, readers and employee access.",
    icon: FiSmartphone,
    items: [
      {
        id: "connect-square",
        question: "How do I connect Square?",
        answer:
          "In the app, open Settings › Billing and tap Connect Square, sign in to Square, and choose your default location. You can do this on any plan, including Free.",
        where: "App › Settings › Billing",
        details: [
          "Square handles the card itself. VendorOrbis never sees or stores card numbers.",
          "Square charges its usual processing fees.",
          "Only the business owner can connect or disconnect Square.",
        ],
        tags: ["App", "All plans", "Owners"],
        keywords: "square connect link account oauth",
      },
      {
        id: "tap-to-pay",
        question: "How do I take Tap to Pay or reader payments?",
        answer:
          "After connecting Square, follow the reader or Tap to Pay setup in Settings › Billing and allow the permissions your phone asks for. On iPhone, the Tap to Pay on iPhone guide is under Menu › Help.",
        details: [
          "Tap to Pay needs a supported phone, an internet connection and location access.",
          "Settings › Preferences shows which permissions are blocked and links to your phone's settings.",
        ],
        tags: ["App", "All plans"],
        keywords: "tap to pay contactless reader nfc iphone android",
      },
      {
        id: "square-locations",
        question: "I have several Square locations. Where do payments go?",
        answer:
          "To your default location, unless you pick a different one for an event. Set it when creating or editing the event.",
        details: [
          "Your list of Square locations refreshes when you connect, and every few minutes while you use Square.",
          "Tax reports can be filtered by Square location on the website.",
        ],
        tags: ["App", "Owners"],
        keywords: "location square multiple stores",
      },
      {
        id: "square-employees",
        question: "Can my employees take card payments with my Square?",
        answer:
          "Yes, if you turn on “Let employees take card payments with my Square” in Settings › Billing. They can use it only while working one of your events, and the money goes to your Square account.",
        details: [
          "Off by default. Disconnecting Square turns it off again.",
          "The employee must be assigned to the event, and the event must still be open.",
          "When an employee switches businesses or signs out, their phone disconnects from your Square.",
        ],
        tags: ["App", "Pro", "Owners", "Employees"],
        keywords: "employee square access staff card payments",
      },
      {
        id: "square-vs-manual",
        question: "What's the difference between Square and Manual card?",
        answer:
          "Square charges the customer's card through your Square account. Manual card just records a card payment you took somewhere else, like another terminal; it doesn't charge anything.",
        tags: ["App"],
        keywords: "manual card terminal record",
      },
    ],
  },
  {
    id: "inventory",
    title: "Inventory & pools",
    blurb: "Items, stock levels, low-stock flags and separate stock pools.",
    icon: FiPackage,
    items: [
      {
        id: "add-item",
        question: "How do I add an item?",
        answer:
          "On the Inventory tab, tap +. Add the name, photo, category, brand, quantity, cost and selling price. Search the list or filter by category and stock to find things later.",
        where: "App › Inventory › +",
        details: [
          "SKUs are unique within your account.",
          "Cost is used to work out your profit in Finance, so it's worth filling in.",
        ],
        tags: ["App", "Website", "Owners"],
        clip: "add-item",
        keywords: "add product item sku price cost photo",
      },
      {
        id: "low-stock",
        question: "How does low stock work?",
        answer:
          "Turn on low stock for an item and set a level, say 10. Once you have that many or fewer, the item gets a red LOW badge in Inventory, and you can filter an event's Sales list to low-stock items.",
        details: [
          "Items selling fastest get a BEST badge.",
        ],
        tags: ["App", "All plans"],
        keywords: "low stock threshold alert reorder badge best seller",
      },
      {
        id: "pools",
        question: "What are inventory pools?",
        answer:
          "Separate stock counts for different places, like a second booth, a storage unit or a shop. Switch between pool tabs in Inventory, and move stock between them with Transfer.",
        where: "App › Inventory › hold the + button",
        tags: ["App", "Pro", "Owners"],
        keywords: "pool location warehouse transfer stock",
      },
      {
        id: "import-inventory",
        question: "Can I import my inventory from a spreadsheet?",
        answer:
          "Yes, on the website. Download the Excel template, fill it in, and upload it. You can also download your current inventory as a spreadsheet.",
        where: "Website › Inventory › Import",
        details: ["Files are .xlsx. You see a preview of what will be imported before anything is saved."],
        tags: ["Website", "Owners"],
        keywords: "import excel spreadsheet xlsx csv bulk upload export",
      },
      {
        id: "categories",
        question: "How do I organize items into categories?",
        answer:
          "Open Menu › Categories to add or rename them, then pick a category on each item. Categories also break down your sales in Finance.",
        tags: ["App"],
        keywords: "category organize group",
      },
    ],
  },
  {
    id: "transactions",
    title: "Refunds & exchanges",
    blurb: "Reviewing sales, refunding, and swapping items.",
    icon: FiRotateCcw,
    items: [
      {
        id: "review-sale",
        question: "Where do I find a past sale?",
        answer:
          "Open the event and go to Transactions. Tap a sale to see its items, payments, discounts and tax.",
        where: "App › Events › an event › Transactions",
        tags: ["App"],
        keywords: "transaction history receipt past sale",
      },
      {
        id: "refund",
        question: "How do I refund a sale?",
        answer:
          "Open the sale in Transactions and tap Refund. Choose the items and quantities, whether each goes back into stock, and how you returned the money.",
        steps: [
          "Open the event › Transactions › the sale.",
          "Tap Refund and pick the items and quantities.",
          "Turn restock on for items you can sell again, off for damaged ones.",
          "Choose the refund method and confirm.",
        ],
        tags: ["App"],
        clip: "refund",
        keywords: "refund return money back",
      },
      {
        id: "refund-square",
        question: "Does a refund in VendorOrbis send the customer's money back?",
        answer:
          "No. VendorOrbis records the refund in your sales, stock and reports, but it doesn't move money. Give the cash back, refund through your QR app, or refund a card payment in Square (Square Dashboard or the Square app).",
        details: [
          "Refunds made in Square still show up correctly in your sales tax reports.",
          "Any commission an employee earned on refunded items is taken back automatically.",
        ],
        tags: ["App"],
        keywords: "refund square money card return",
      },
      {
        id: "exchange",
        question: "How do I exchange an item?",
        answer:
          "Open the sale and choose Exchange. Pick the item coming back and what the customer takes instead; the returned item goes back into stock unless you turn that off.",
        details: ["Custom items (typed in at checkout) can't be put back into stock."],
        tags: ["App"],
        keywords: "exchange swap size color",
      },
    ],
  },
  {
    id: "finance",
    title: "Finance & reports",
    blurb: "Profit, event reports, expenses, tax reports and the Finance lock.",
    icon: FiPieChart,
    items: [
      {
        id: "finance-overview",
        question: "What does Finance show?",
        answer:
          "Net profit, revenue, total cost and average sale for the dates you pick, plus refunds, discounts, employee pay and business expenses. Below that, sales broken down by payment method, product and category.",
        where: "App › Finance",
        details: [
          "Date presets include Today, Last 7/30/90 days, This month and All time, or pick custom dates.",
        ],
        tags: ["App", "Website", "Owners"],
        keywords: "profit revenue report dashboard finance overview",
      },
      {
        id: "event-report",
        question: "What's in an event's financial report?",
        answer:
          "Everything about one show: revenue, profit margin, your peak hour, best-selling products, and sales by category, brand and payment method. Multi-day events can be viewed day by day, and you can compare against a past event.",
        where: "App › Finance › an event",
        details: [
          "When comparing multi-day events, Day 2 is compared with Day 2 of the other event, not the same calendar date.",
          "Pick last year's edition of the same market to see how it changed.",
        ],
        tags: ["App", "Owners"],
        clip: "event-report",
        keywords: "event report best sellers hourly peak compare year over year",
      },
      {
        id: "expenses",
        question: "How do I add expenses?",
        answer:
          "Booth fees and other costs for one show go on that event as additional costs. Everything else, like supplies or insurance, is a business expense from the + button on Finance.",
        tags: ["App", "Owners"],
        keywords: "expense cost booth fee overhead",
      },
      {
        id: "tax-reports",
        question: "Can I get a sales tax report?",
        answer:
          "Yes, on the website under Finance › Tax reports. It totals taxable sales and tax collected by month and tax rate, can include your Square payments, and downloads as CSV for filing.",
        where: "Website › Finance › Tax reports",
        tags: ["Website", "Owners"],
        keywords: "sales tax report filing csv monthly jurisdiction",
      },
      {
        id: "finance-lock",
        question: "Can I hide Finance from people using my phone?",
        answer:
          "Yes. Set a Finance passcode in Settings › Security, and Finance will ask for it before opening. Employees never see the Finance tab.",
        where: "App › Settings › Security",
        tags: ["App", "Owners"],
        keywords: "passcode lock pin hide finance privacy",
      },
      {
        id: "numbers-differ",
        question: "Why don't two reports show the same numbers?",
        answer:
          "Usually they cover different dates or events. Check the date range first, then refunds, discounts, item costs, expenses and staff pay.",
        tags: ["App", "Website"],
        keywords: "wrong numbers mismatch different totals",
      },
    ],
  },
  {
    id: "team",
    title: "Team & staff pay",
    blurb: "Employees, pay and commission, and working for more than one business.",
    icon: FiUsers,
    items: [
      {
        id: "add-employee",
        question: "How do I add an employee?",
        answer:
          "In Settings › Team, invite them by email. Once they accept, assign them to events. They sign in with their own VendorOrbis account.",
        where: "App › Settings › Team",
        tags: ["App", "Pro", "Owners"],
        keywords: "employee staff invite team member hire",
      },
      {
        id: "employee-access",
        question: "What can employees see and do?",
        answer:
          "Employees see the Events tab and sell at the events they're assigned to. They don't see Finance, Inventory or Insight, but they can check their own earnings for each event.",
        details: [
          "Card payments use your Square only if you allow it; see Square & Tap to Pay.",
          "In the app, an employee's earnings card is called My Earnings.",
        ],
        tags: ["App", "Employees"],
        keywords: "employee permissions role access",
      },
      {
        id: "staff-pay",
        question: "How do staff pay and commission work?",
        answer:
          "Give each employee a base pay (none, hourly or per event) and an optional commission on what they sell. Decide whether commission is on top of base pay, instead of it, or whichever pays more, and optionally a sales minimum before commission starts.",
        steps: [
          "Set default pay in Settings › Team › the employee.",
          "Adjust pay, hours or mark them paid per event: open the event › Transactions › Staff Pay.",
        ],
        details: [
          "Hours default to the event's scheduled hours; change them per event.",
          "Commission comes from the employee's own sales lines, so refunds and exchanges reduce it automatically.",
          "Staff pay is included in event and overall profit.",
        ],
        tags: ["App", "Pro", "Owners"],
        clip: "staff-pay",
        keywords: "staff pay commission wages hourly payroll earnings",
      },
      {
        id: "switch-business",
        question: "I work for someone and run my own booth. Can I do both?",
        answer:
          "Yes. Use the business switcher in the menu to move between your employer's business and your own. Start your own business from Settings › Team if you haven't yet.",
        details: [
          "You can work for one employer at a time.",
          "Your own plan applies in your own business; your employer's plan applies in theirs.",
          "Switching reloads the app with that business's data.",
        ],
        tags: ["App", "Website", "Employees"],
        keywords: "switch business workspace employer own business",
      },
    ],
  },
  {
    id: "insight",
    title: "Insight & planning",
    blurb: "The business advisor and what to bring to your next show.",
    icon: FiTrendingUp,
    items: [
      {
        id: "insight-overview",
        question: "What does Insight show?",
        answer:
          "A business advisor built from your own sales: business health, things that need attention, plans for upcoming events, which events are worth repeating, product opportunities and when you sell most.",
        where: "App › Insight",
        details: [
          "Only owners on Pro see Insight.",
          "A Data Quality section tells you when missing costs or too few sales make the advice less reliable.",
        ],
        tags: ["App", "Pro", "Owners"],
        keywords: "insight advisor ai analytics recommendations",
      },
      {
        id: "restock-forecast",
        question: "How does Insight know what to bring to my next show?",
        answer:
          "It looks at what sold at similar past events (same market name or location, recent shows first) and suggests how many of each item to bring. Tap Add recommended stock to event to allocate it in one go.",
        details: [
          "Selling out counts as higher demand, more so the earlier it ran out.",
          "The suggested amount covers a strong day, not just an average one, and is scaled to the event's selling hours.",
          "It checks stock already sent to the event and spreads what's at home across your next three events.",
        ],
        tags: ["App", "Pro", "Owners"],
        clip: "restock-plan",
        keywords: "forecast restock how much to bring prediction",
      },
    ],
  },
  {
    id: "website",
    title: "Website tools",
    blurb: "What you can do at vendororbis.com.",
    icon: FiMonitor,
    items: [
      {
        id: "pdf-editor",
        question: "What is the PDF editor?",
        answer:
          "A tool for filling in and editing PDFs, like vendor applications, right in your browser.",
        details: ["Your PDFs stay in your browser. They aren't uploaded to our servers."],
        tags: ["Website"],
        keywords: "pdf edit form application sign",
      },
      {
        id: "booth-layout",
        question: "What is the booth layout planner?",
        answer:
          "A free tool under Tools for sketching your booth: tables, racks and displays on a grid sized to your space.",
        where: "Website › Tools",
        tags: ["Website"],
        keywords: "booth layout planner design tent table",
      },
      {
        id: "live-auctions",
        question: "What are live auctions?",
        answer:
          "Tools for selling on TikTok LIVE: track auction sessions, buyers and wins, bundle orders, and see the money from each live.",
        where: "Website › Live auctions",
        tags: ["Website", "Lite & Pro"],
        keywords: "tiktok live auction stream",
      },
    ],
  },
  {
    id: "account",
    title: "Data, privacy & account",
    blurb: "How your data is protected, and closing your account.",
    icon: FiShield,
    items: [
      {
        id: "data-security",
        question: "How is my data protected?",
        answer:
          "Everything is sent over encrypted connections, passwords are stored as one-way hashes, and card details are handled by Square, never by us. Your data is visible only to your account and the employees you invite.",
        details: [
          "Employees can only reach the events they're assigned to.",
          "See the Privacy Policy for what we collect and who we share it with.",
        ],
        tags: ["App", "Website"],
        keywords: "security privacy encryption safe data",
      },
      {
        id: "delete-account",
        question: "How do I close or delete my account?",
        answer:
          "Settings › Account › Archive account closes it; signing in again reopens it. To permanently delete your account and all its data, email support@vendororbis.com.",
        details: ["Cancel any App Store or Google Play subscription in the store too; archiving doesn't cancel it."],
        where: "App › Settings › Account",
        tags: ["App", "Owners"],
        keywords: "delete close remove account archive data",
      },
    ],
  },
  {
    id: "support",
    title: "Help & contact",
    blurb: "Reporting problems and reaching us.",
    icon: FiLifeBuoy,
    items: [
      {
        id: "report-problem",
        question: "How do I report a problem or suggest a feature?",
        answer:
          "Use Menu › Help in the app for suggestions, the changelog and our Discord community, or email support@vendororbis.com. Tell us the screen, what you were doing, and add a screenshot.",
        details: [
          "Check the account you're signed in to, the event, the inventory pool and the date range first; they cause most “missing data” reports.",
        ],
        tags: ["App", "Website"],
        keywords: "bug problem help contact support discord feedback",
      },
    ],
  },
];

/** Shown as shortcuts at the top of the page. */
export const POPULAR_QUESTION_IDS = [
  "plans-compare",
  "offline",
  "connect-square",
  "refund-square",
  "staff-pay",
  "where-to-subscribe",
];
